import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { errorToastMessage } from "../../../utils/toast";
import http from "../../../utils/http";
import PhaseList from "../../Responses/PhaseList";
import QuestionItemWrapper from "../../Responses/QuestionItemWrapper";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { getFormData } from "../../../Redux/actions/responseAction";
import { SxProps } from "@mui/material";
import { Stack } from "@mui/system";
import {
  resetResponseState,
  setIsRegistryParticipant,
  setPhaseList,
  setResponseIsEditable,
  setResponseLoader,
  setScreeningAttemptId,
  setScreeningBtn,
  setSelectedPhase,
} from "../../../Redux/reducers/responseSlice";
import { VisitPhaseRoot } from "../../../types/VisitPhases.types";
import { PhasesList } from "../../Responses/types";
import { handlePhaseStatus } from "../../Responses/utils";
import { buildPhaseListForm } from "../../Responses/formCompletion";
import FinishReviewModal from "../../Responses/Screening/FinishReviewModal";
import ConfirmationModalWrapper from "../../Responses/ConfirmationModalWrapper";
import CalculationWrapper from "../../Responses/CalculationWrapper/CalculationWrapper";
import { platformAdminPermissions } from "../../../utils/roles";

const rootResponses: SxProps = {
  width: "100%",
  height: "calc(100vh - 182px)",
  display: "flex",
  p: 1,
  overflow: "auto",
  position: "relative",
  alignItems: "flex-start",
};

const ScreeningResponses = ({
  showModal,
  setShowModal,
  isLocked,
}: {
  showModal: boolean;
  setShowModal: (v: boolean) => void;
  isLocked?: boolean;
}) => {
  const { participantId } = useParams();
  const dispatch = useAppDispatch();

  const platformPermissions = useAppSelector(
    (state) => state.user.platformPermissions,
  );

  // Edit capability requires both the permission AND a non-locked participant.
  const [hasEditPermission] = useState(
    platformPermissions.includes(platformAdminPermissions.managePlatform) ||
      false,
  );
  const canEdit = hasEditPermission && !isLocked;

  const [isLoading, setIsLoading] = useState(false);
  const togglePhase = useAppSelector((state) => state.response.togglePhase);
  const selectedPhase = useAppSelector((state) => state.response.selectedPhase);
  const eligibilityStatus = useAppSelector(
    (state) => state.response.eligibilityStatus,
  );
  const toggleLoader = useAppSelector((state) => state.response.toggleLoader);

  const fetchStudyPhases = useCallback(async () => {
    try {
      if (participantId) {
        dispatch(setIsRegistryParticipant(true));
        setIsLoading(true);
        dispatch(setResponseLoader(true));
        let params: any = {
          participantId,
        };

        const attemptsRes = await http.get(
          `registry/admin-screening-responses/attempts`,
          { params },
        );

        const attempts = attemptsRes.data?.data || [];
        const participantAttempt = attempts.find(
          (attempt: any) => attempt.actorId === null,
        );
        const adminAttempt = attempts.find(
          (attempt: any) => attempt.actorId !== null,
        );
        const attemptId = adminAttempt
          ? adminAttempt.id
          : participantAttempt?.id;

        if (!attemptId) {
          dispatch(setResponseIsEditable(canEdit));
          dispatch(setScreeningBtn("create"));
          dispatch(setResponseLoader(false));
          setIsLoading(false);
          return;
        }

        dispatch(
          setResponseIsEditable(
            canEdit ? (adminAttempt ? !adminAttempt.isReadOnly : false) : false,
          ),
        );
        dispatch(
          setScreeningBtn(
            adminAttempt
              ? participantAttempt
                ? "compare"
                : "complete"
              : "create",
          ),
        );
        dispatch(
          setScreeningAttemptId({
            screeningAttemptId: attemptId,
          }),
        );

        const { data } = await http.get<VisitPhaseRoot>(
          `/admin/registry/registry-phase/participant/screening`,
          { params },
        );

        if (data?.data.length === 0) {
          // Set Reducer Form Loading State to false
          dispatch(setResponseLoader(false));
          setIsLoading(false);
          return;
        }

        const phases: PhasesList[] = data?.data
          .map((phase) => {
            const phaseForms = phase.phaseForms
              .map((phaseForm) => {
                const formAttempt = phaseForm.form.formAttempts
                  ? phaseForm.form.formAttempts.find(
                      (attempt: any) =>
                        attempt?.screeningAttempt?.id === attemptId,
                    )
                  : null;
                return buildPhaseListForm(phaseForm, phase, formAttempt);
              })
              .sort((a, b) => a.position - b.position);

            const phaseStatus = handlePhaseStatus(phaseForms);

            return {
              id: phase.id,
              name: phase.name,
              position: phase.position,
              phaseStatus: phaseStatus,
              phaseForms: phaseForms,
            };
          })
          .sort((a, b) => a.position - b.position);

        let selectedForm = null;
        if (phases.length > 0) {
          const formId = sessionStorage.getItem("response-visit-form");
          let selPhase = phases?.[0]?.id;

          if (formId) {
            phases.forEach((phase) => {
              const selForm = phase.phaseForms.find((pf) => pf.id === formId);
              if (selForm) {
                selPhase = phase.id;
                selectedForm = selForm;
              }
            });
          }
          if (!selectedForm) {
            const allForms = phases.flatMap((p) =>
              (p.phaseForms || []).map((pf) => ({ phaseId: p.id, form: pf })),
            );
            const screeningEditable = adminAttempt
              ? !adminAttempt.isReadOnly
              : false;
            let pick = null;
            if (!screeningEditable) {
              pick =
                allForms.find((item) => !!item.form.attempt) || allForms[0];
            } else {
              const incomplete = allForms.find(
                (item) => item.form.status !== "completed",
              );
              const inProgress = allForms.find(
                (item) =>
                  item.form.status === "active" ||
                  item.form.status === "inprogress",
              );
              pick = incomplete || inProgress || allForms[0];
            }
            if (pick) {
              selectedForm = pick.form;
              selPhase = pick.phaseId;
            }
          }
          if (selectedForm) {
            dispatch(getFormData({ selectedForm, participantId }));
          } else {
            dispatch(setResponseLoader(false));
          }
          dispatch(setSelectedPhase({ phase: selPhase }));
        }

        dispatch(setPhaseList({ list: phases }));
        setIsLoading(false);
      }
    } catch (error) {
      dispatch(setResponseLoader(false));
      setIsLoading(false);
      errorToastMessage(error as Error);
    }
  }, [participantId, dispatch, togglePhase, canEdit, toggleLoader]);

  useEffect(() => {
    fetchStudyPhases();
  }, [fetchStudyPhases]);

  useEffect(() => {
    return () => {
      sessionStorage.removeItem("response-visit-form");
      dispatch(resetResponseState());
    };
  }, [dispatch]);

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <Stack direction={"row"} sx={rootResponses}>
      {/* Phases and Forms */}
      <PhaseList
        isLoading={isLoading}
        selectedPhaseId={selectedPhase}
        currentTab={"Screening"}
        showCollapse={true}
      />

      {/* Question List */}
      <QuestionItemWrapper />
      {showModal && (
        <FinishReviewModal
          showModal={showModal}
          closeModal={closeModal}
          changeStatus={eligibilityStatus === "decide_later"}
        />
      )}

      <ConfirmationModalWrapper />
      <CalculationWrapper />
    </Stack>
  );
};
export default ScreeningResponses;
